import "@/i18n/config";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { useQuery } from "react-query";

import { Pagination } from "@/client_app/components/Pagination";
import { ADMIN_COMMUNITY_MEMBER_PATH } from "@/organization_app/config";
import {
  createReferrals,
  getReferrals,
  updateReferrals,
} from "@/organization_app/features/jobs/services/FetchAPIService";
import { PrimaryButton, SecondaryButton } from "@/ui/buttons";

const limit = 25;

export function ReferralsPage() {
  const [paginationOffset, setPaginationOffset] = useState(0);
  const [candidates, setCandidates] = useState([]);
  const [backupData, setBackupData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [newData, setNewData] = useState({});
  const [errorData, setErrorData] = useState();

  const queryParams = {
    limit: limit,
    offset: paginationOffset,
  };

  const { data, isFetching, isSuccess, error } = useQuery(
    ["referrals", queryParams],
    () =>
      getReferrals(queryParams).then((response) => {
        return response.json();
      })
  );

  useEffect(() => {
    if (data) {
      setCandidates(data.candidates);
      setTotalCount(data.total_count);
    }
  }, [data]);
  const toggleEditMode = (index: number, enable: boolean) => {
    const updatedData = [...candidates];
    updatedData[index] = {
      ...updatedData[index],
      isEditing: enable,
    };

    if (enable) {
      // Save a backup of the row data when entering edit mode
      setBackupData((prev) => ({
        ...prev,
        [index]: { ...candidates[index] },
      }));
    } else {
      // Clear the backup when exiting edit mode
      setBackupData((prev) => {
        const updatedBackup = { ...prev };
        delete updatedBackup[index];
        return updatedBackup;
      });
    }

    setCandidates(updatedData);
  };

  const cancelEdit = (index: number) => {
    // Revert to the original data for existing rows
    const updatedData = [...candidates];
    updatedData[index] = { ...backupData[index], isEditing: false }; // Restore original data and exit edit mode
    setCandidates(updatedData);
    setBackupData((prev) => {
      const updatedBackup = { ...prev };
      delete updatedBackup[index];
      return updatedBackup;
    });
  };

  const handleInputChange = (index: number, key: string, value: any) => {
    const updatedData = [...candidates];
    updatedData[index] = { ...updatedData[index], [key]: value };
    setCandidates(updatedData);
  };

  const saveRow = (index: number) => {
    const rowData = candidates[index];

    updateReferrals({ id: rowData.id, status: rowData.status })
      .then((response) => response.json())
      .then((data) => {
        setCandidates((prevData) => {
          const updatedData = [...prevData];
          updatedData[index] = {
            ...updatedData[index],
            ...data.candidate,
            isEditing: false,
          };
          return updatedData;
        });
      });
  };

  const handleCreate = () => {
    const formData = new FormData();

    formData.append("referral[name]", newData["name"]);
    formData.append("referral[email]", newData["email"]);
    formData.append("referral[mobile_number]", newData["mobile_number"]);
    if (newData["document"]) {
      formData.append("referral[document]", newData["document"]);
    }

    createReferrals(formData)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          setErrorData(data.error);
        } else {
          setCandidates((prevData) => {
            return [data.candidate, ...prevData];
          });
          handleCloseModal();
        }
      });
  };
  const handleCloseModal = () => {
    setIsOpenModal(false);
    setNewData({});
    setErrorData(null);
  };

  if (error) {
    return <div>Error!</div>;
  }

  const tableCellStyleClasses =
    "border border-neutral-200 border-l-0 border-r-0 first:border-l last:border-r first:rounded-tl-xl first:rounded-bl-xl last:rounded-br-xl last:rounded-tr-xl py-4 px-5 first:pl-6 last:pr-6";
  const options = [
    ["submitted", "Submitted"],
    ["processing", "Processing"],
    ["employed", "Employed"],
    ["paid", "Paid"],
    ["rejected", "Rejected"],
  ];

  const customStyles = {
    overlay: {
      zIndex: 9999,
      backgroundColor: "rgba(9, 32, 36, 0.9)",
    },
    content: {
      background: "none",
      border: "none",
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      padding: "0",
    },
  };

  const modal = (
    <Modal isOpen={true} style={customStyles}>
      <div className='animated fadeIn fixed inset-0 overflow-y-auto flex items-center justify-center'>
        <div className='max-h-screen w-full max-w-xl relative'>
          <div className='bg-legacyColor-secondary-50 rounded-3xl border border-legacyColor-neutral-2-500 p-4 '>
            <div className='p-6'>
              <div className='text-center'>
                <h1 className='h3 mb-4'>New Referral</h1>
              </div>
              <div className='flex flex-col pb-4'>
                <label className='text-sm font-semibold'>Name</label>
                <input
                  type='text'
                  className='form-control'
                  placeholder='Name'
                  onChange={(e) =>
                    setNewData({ ...newData, name: e.target.value })
                  }
                />
              </div>
              <div className='flex flex-col pb-4'>
                <label className='text-sm font-semibold'>Mobile Number</label>
                <input
                  type='text'
                  className='form-control'
                  placeholder='Mobile Number'
                  onChange={(e) =>
                    setNewData({ ...newData, mobile_number: e.target.value })
                  }
                />
              </div>
              <div className='flex flex-col pb-4'>
                <label className='text-sm font-semibold'>
                  Referral by Weployee email
                </label>
                <input
                  type='text'
                  className='form-control'
                  placeholder='Weployee email'
                  onChange={(e) =>
                    setNewData({ ...newData, email: e.target.value })
                  }
                />
              </div>
              <div className='flex flex-col pb-4'>
                <label className='text-sm font-semibold'>Resume</label>
                <input
                  type='file'
                  accept='.pdf,.doc,.docx,.jpg,.png'
                  onChange={(e) =>
                    setNewData({
                      ...newData,
                      document: e.target.files?.[0] || null,
                    })
                  }
                />
              </div>
              {errorData && <div className='text-error-600'>{errorData}</div>}
            </div>
            <div className='text-center w-full pt-2 flex justify-between'>
              <button
                className='btn btn-sm btn-secondary'
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                className='btn btn-sm btn-primary'
                onClick={handleCreate}
                disabled={
                  !newData["name"] ||
                  !newData["mobile_number"] ||
                  !newData["email"]
                }
              >
                Create
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
  return (
    <div className='text-legacyColor-neutral-1-500'>
      <header className='px-12 py-8 bg-legacyColor-neutral-2-100'>
        <h1 className='text-headline2 mb-4'>Referral Candidates</h1>
      </header>
      <section className='m-12'>
        {isFetching && <div>Loading...</div>}

        {!isFetching && isSuccess && (
          <div>
            <PrimaryButton
              label='New Referral'
              size='SMALL'
              onClick={() => setIsOpenModal(true)}
            />
            {isOpenModal && modal}
            {candidates.length === 0 && "No data"}
            {candidates.length > 0 && (
              <>
                <table
                  className='w-full text-center border-separate table-auto'
                  style={{ borderSpacing: "0 24px" }}
                >
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Submitted Date</th>
                      <th>Mobile Number</th>
                      <th>By Weployee</th>
                      <th>Status</th>
                      <th>Start Date</th>
                      <th>Resume</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((candidate, index) => (
                      <tr key={index}>
                        <td className={clsx(tableCellStyleClasses)}>
                          <div className='text-md font-semibold'>
                            {candidate["name"]}
                          </div>
                        </td>

                        <td className={clsx(tableCellStyleClasses)}>
                          {new Date(
                            candidate["submitted_date"]
                          ).toLocaleDateString("en-GB")}
                        </td>

                        <td className={clsx(tableCellStyleClasses)}>
                          {candidate["mobile_number"]}
                        </td>

                        <td className={clsx(tableCellStyleClasses)}>
                          <a
                            href={ADMIN_COMMUNITY_MEMBER_PATH(
                              candidate["community_member_id"]
                            )}
                            target='_blank'
                            className='underline'
                          >
                            {candidate["community_member_name"]}
                          </a>
                        </td>
                        <td
                          className={`${clsx(
                            tableCellStyleClasses
                          )} capitalize`}
                        >
                          {candidate["isEditing"] ? (
                            <select
                              defaultValue={candidate["status"]}
                              className='form-control'
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "status",
                                  e.target.value
                                )
                              }
                            >
                              {options.map(([key, value]) => (
                                <option key={key} value={key}>
                                  {value}
                                </option>
                              ))}
                            </select>
                          ) : (
                            candidate["status"]
                          )}
                        </td>
                        <td className={clsx(tableCellStyleClasses)}>
                          {candidate["start_date"] &&
                            new Date(
                              candidate["start_date"]
                            ).toLocaleDateString("en-GB")}
                        </td>
                        <td className={clsx(tableCellStyleClasses)}>
                          <a
                            href={candidate["document_url"]}
                            target='_blank'
                            className='underline'
                          >
                            Attached
                          </a>
                        </td>
                        <td className={clsx(tableCellStyleClasses)}>
                          {candidate["isEditing"] ? (
                            <div className='flex gap-2'>
                              <PrimaryButton
                                label='Save'
                                size='SMALL'
                                onClick={() => saveRow(index)}
                              />
                              <SecondaryButton
                                label='Cancel'
                                size='SMALL'
                                onClick={() => cancelEdit(index)}
                              />
                            </div>
                          ) : (
                            <SecondaryButton
                              label='Edit'
                              size='SMALL'
                              onClick={() => toggleEditMode(index, true)}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pagination
                  totalCount={totalCount}
                  onPageChange={(data) => setPaginationOffset(data)}
                  perPage={limit}
                />
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
